import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import {
  FiArrowRight,
  FiClock,
  FiSun,
  FiMapPin,
  FiX,
} from "react-icons/fi";
import { Button } from "../../ui/Button";
import * as D from "./Farms.desktop.styled";

const FarmModalDesktop = ({
  farm,
  onClose,
  isSaved,
  onToggleWishlist,
  deliveryLabel,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!farm) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", handleKey);
    };
  }, [farm, onClose]);

  return (
    <AnimatePresence>
      {farm ? (
        <D.ModalOverlay
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
          role="presentation"
        >
          <D.ModalFrame
            as={motion.div}
            initial={{ opacity: 0, scale: reduceMotion ? 1 : 0.92, y: reduceMotion ? 0 : 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: reduceMotion ? 1 : 0.96, y: reduceMotion ? 0 : 10 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 26,
            }}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={farm.name}
          >
            <D.ModalMedia>
              <D.ModalImage src={farm.image} alt={farm.name} />
              <D.ModalMediaScrim aria-hidden="true" />
              <D.ModalClose
                type="button"
                onClick={onClose}
                aria-label={t("common.close", { defaultValue: "Close" })}
              >
                <FiX aria-hidden="true" />
              </D.ModalClose>
              <D.ModalTitleBlock>
                {farm.isOrganic ? (
                  <D.ModalKicker>
                    <FiSun aria-hidden="true" />
                    {t("farms.badge.organic", { defaultValue: "Organic" })}
                  </D.ModalKicker>
                ) : null}
                <D.ModalTitle>{farm.name}</D.ModalTitle>
              </D.ModalTitleBlock>
            </D.ModalMedia>

            <D.ModalBody>
              <D.ModalMeta>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <FiMapPin aria-hidden="true" />
                  {farm.location}
                </span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <FiClock aria-hidden="true" />
                  {deliveryLabel}
                </span>
              </D.ModalMeta>

              {farm.tags && farm.tags.length ? (
                <D.TagRow>
                  {farm.tags.map((tag) => (
                    <D.Tag key={`${farm.id}-modal-${tag}`}>{tag}</D.Tag>
                  ))}
                </D.TagRow>
              ) : null}

              <D.ModalDescription>{farm.description}</D.ModalDescription>

              <D.ModalActions>
                <Button
                  type="button"
                  variant="primary"
                  iconRight={<FiArrowRight aria-hidden="true" />}
                  onClick={() => {
                    onClose();
                    navigate(`/farms/${farm.id}`);
                  }}
                >
                  {t("farms.card_button", { defaultValue: "View Farm" })}
                </Button>
                {onToggleWishlist ? (
                  <Button
                    type="button"
                    variant="outline"
                    iconLeft={isSaved ? <FaHeart /> : <FaRegHeart />}
                    onClick={(event) => onToggleWishlist(event, farm.id)}
                  >
                    {isSaved
                      ? t("farms.wishlist.saved", { defaultValue: "Saved" })
                      : t("farms.wishlist.save", { defaultValue: "Save" })}
                  </Button>
                ) : null}
              </D.ModalActions>
            </D.ModalBody>
          </D.ModalFrame>
        </D.ModalOverlay>
      ) : null}
    </AnimatePresence>
  );
};

export default FarmModalDesktop;
